import { parsePhoneNumber } from 'libphonenumber-js';

/**
 * Format international phone number by extracting national number (without country code)
 * Supports 200+ countries via libphonenumber-js
 * 
 * @param {string} input - Phone number input (can include country code, spaces, special chars)
 * @returns {string} - National number without country code
 * 
 * Examples:
 * - "+91 98765 43210" → "9876543210"
 * - "+1 (555) 123-4567" → "5551234567"
 * - "+44 20 7946 0958" → "2079460958"
 * - "+971 50 123 4567" → "501234567"
 */
export const formatInternationalPhoneNumber = (input) => {
    if (!input) return '';
    
    // Remove all non-digit characters except + sign
    const cleanedText = input.replace(/[^\d+]/g, '');
    
    try {
        // Try to parse as international phone number
        const phoneNumber = parsePhoneNumber(cleanedText);
        
        if (phoneNumber && phoneNumber.nationalNumber) {
            // Return the national number (without country code)
            return phoneNumber.nationalNumber;
        }
    } catch (e) {
        // If parsing fails, use fallback logic
        console.log('[PhoneUtils] Parsing failed, using fallback logic:', e.message);
    }
    
    // Fallback: Extract digits only
    const digitsOnly = cleanedText.replace(/\D/g, '');
    
    // Pattern matching for common country codes
    if (digitsOnly.length > 10) {
        // India: +91 XXXXXXXXXX (12 digits total)
        if (digitsOnly.startsWith('91') && digitsOnly.length === 12) {
            return digitsOnly.substring(2);
        }
        // USA/Canada: +1 XXXXXXXXXX (11 digits total)
        else if (digitsOnly.startsWith('1') && digitsOnly.length === 11) {
            return digitsOnly.substring(1);
        }
        // UK: +44 XXXXXXXXXX (12 digits total)
        else if (digitsOnly.startsWith('44') && digitsOnly.length === 12) {
            return digitsOnly.substring(2);
        }
        // UAE: +971 XXXXXXXXX (12 digits total)
        else if (digitsOnly.startsWith('971') && digitsOnly.length === 12) {
            return digitsOnly.substring(3);
        }
        // Singapore: +65 XXXXXXXX (10 digits total)
        else if (digitsOnly.startsWith('65') && digitsOnly.length === 10) {
            return digitsOnly.substring(2);
        }
        // Australia: +61 XXXXXXXXX (11 digits total)
        else if (digitsOnly.startsWith('61') && digitsOnly.length === 11) {
            return digitsOnly.substring(2);
        }
        // For unknown patterns, take last 10 digits as fallback
        else {
            return digitsOnly.length > 10 ? digitsOnly.slice(-10) : digitsOnly;
        }
    }
    
    return digitsOnly;
};

/**
 * Validate phone number (supports international numbers)
 * 
 * @param {string} phoneNumber - Phone number to validate
 * @returns {object} - { isValid: boolean, errorMessage: string }
 */
export const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) {
        return { isValid: false, errorMessage: 'Please enter a phone number' };
    }
    
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Support 7-15 digits (E.164 international standard)
    if (cleanNumber.length < 7 || cleanNumber.length > 15) {
        return { 
            isValid: false, 
            errorMessage: 'Phone number must be between 7-15 digits' 
        };
    }
    
    // For Indian numbers (10 digits starting with 6-9), apply Indian-specific validation
    // For other 10-digit numbers or numbers of different lengths, accept them as valid
    if (cleanNumber.length === 10 && /^[6-9]/.test(cleanNumber)) {
        // This is likely an Indian number - already validated by the regex
        return { isValid: true, errorMessage: '' };
    }
    
    // For all other valid length numbers (7-15 digits), accept them
    return { isValid: true, errorMessage: '' };
};

/**
 * Handle phone input change with international formatting
 * Use this in onChange handlers for phone input fields
 * 
 * @param {string} value - Input value from the field
 * @returns {string} - Formatted national number
 */
export const handlePhoneInputChange = (value) => {
    return formatInternationalPhoneNumber(value);
};
